import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    Renderer2,
    RendererStyleFlags2,
    ViewChild,
    inject
} from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { OnChange } from '@df/ng/on-change';
import { AbstractPlayerComponent } from '@df/ui/display/media/abstract-player.component';
export interface IVimeoPlayerOptions {
    id?: string | number;
    autopause: 0 | 1;
    autoplay: 0 | 1;
    loop: 0 | 1;
    background: 0 | 1;
    muted: 0 | 1;
    color: string;
    controls: 0 | 1;
    title: 0 | 1;
    byline?: 0 | 1;
    portrait?: 0 | 1;
    badge?: 0 | 1;
    dnt?: 0 | 1;
    progress_bar?: 0 | 1;
}

export const vimeoDefaultOptions: IVimeoPlayerOptions = {
    autopause: 0,
    autoplay: 0,
    loop: 0,
    background: 0,
    muted: 0,
    color: 'ffffff',
    controls: 1,
    title: 0,
    byline: 0,
    portrait: 0,
    badge: 0,
    dnt: 0
};

export interface IVimeoPlayerProgressEvent {
    duration: number;
    percent: number;
    seconds: number;
}

/**
 * Vimeo player.
 *
 * @example
 * ```html
 * <vimeo-player video-id="1234567"></vimeo-player>
 * ```
 */
@Component({
    selector: 'vimeo-player',
    template: `
        <div #container></div>
        <div
            class="video-player-control"
            [class.is-playing]="isPlaying"
            *ngIf="(!background && ((!autoplay && !autoplayInViewport) || isPlaying || showControl)) || controls"
        >
            <div class="icon-control icon-video-play" *ngIf="playButton && !isPlaying" (click)="play()">
                <ng-container *ngTemplateOutlet="playButton; context: { $implicit: this }"></ng-container>
            </div>
            <div class="icon-control icon-video-pause" *ngIf="pauseButton && isPlaying" (click)="pause()">
                <ng-container *ngTemplateOutlet="pauseButton; context: { $implicit: this }"></ng-container>
            </div>
        </div>
        <ng-container *ngTemplateOutlet="customTpl!; context: { $implicit: this }"></ng-container>
        <div class="video-player-progress" [class.is-playing]="isPlaying" [class.show-control]="showControl" *ngIf="progressBar">
            <div #videoProgressBar class="video-player-progress-percent"></div>
        </div>
    `,
    standalone: true,
    imports: [CommonModule],
    host: {
        '[class.video-player-container]': '!!componentId',
        ngSkipHydration: 'true'
    },
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VimeoPlayerComponent extends AbstractPlayerComponent<VimeoPlayerComponent, IVimeoPlayerProgressEvent> implements OnInit {
    protected static PLAYER_URL = '//player.vimeo.com/video/';
    protected static PLAYER_API_URL = '//player.vimeo.com/api/player.js';
    protected static PLAYER_ID_REGEXP = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/;
    protected static API: any;
    protected static WAIT = 0;
    protected static MAX_WAIT = 10;

    /** @internal */
    protected override componentId = 'vimeo-player';

    /** @internal */
    protected iframe?: HTMLElement;

    /** @internal */
    protected options: IVimeoPlayerOptions = { ...vimeoDefaultOptions };

    isReady = false;

    private renderer2 = inject(Renderer2);

    /**
     * Display video as background - autoplay, loop and no controls - available only for Plus, PRO or Business members.
     *
     * @type {boolean}
     */
    @Input()
    @OnChange<boolean>(function (this: VimeoPlayerComponent, value: boolean) {
        this.autoplay = this.autoplay !== undefined ? this.autoplay : coerceBooleanProperty(value);
        this.loop = this.loop !== undefined ? this.loop : coerceBooleanProperty(value);
        this.controls = this.controls !== undefined ? this.controls : value ? false : true;
        this.muted = this.muted !== undefined ? this.muted : value ? true : false;

        this.options.background = value ? 1 : 0;

        (this.renderer2 as Renderer2).setStyle(this.elementRef.nativeElement, 'pointerEvents', value ? 'none' : null);
        value
            ? (this.renderer2 as Renderer2).addClass(this.elementRef.nativeElement, 'is-background')
            : (this.renderer2 as Renderer2).removeClass(this.elementRef.nativeElement, 'is-background');
    })
    background?: boolean;

    @Input()
    @OnChange<boolean>(function (this: VimeoPlayerComponent, value: boolean) {
        this.options.loop = value ? 1 : 0;
    })
    override loop?: boolean;

    @Input()
    @OnChange<boolean>(function (this: VimeoPlayerComponent, value: boolean) {
        this.options.controls = value ? 1 : 0;
    })
    controls?: boolean;

    @Input()
    @OnChange<boolean>(function (this: VimeoPlayerComponent, value: boolean) {
        this.options.muted = this.autoplay || this.autoplayInViewport ? 1 : value ? 1 : 0;
    })
    muted?: boolean;

    @Input()
    @OnChange<boolean>(function (this: VimeoPlayerComponent, value: boolean) {
        if (value) {
            this.muted = value;
            this.options.autoplay = 1
        }
    })
    override autoplay?: boolean;

    @Input('autoplay-in-viewport')
    @OnChange<boolean>(function (this: VimeoPlayerComponent, value: boolean) {
        if (value) {
            this.muted = value;
        }
    })
    override autoplayInViewport?: boolean;

    /**
     * do not track, removes vimeo user relation in embedded player, player is visible as not logged into vimeo
     */
    @Input()
    @OnChange<boolean>(function (this: VimeoPlayerComponent, value: boolean) {
        this.options.dnt = value ? 1 : 0;
    })
    dnt?: boolean;

    @Input()
    @OnChange<boolean>(function (this: VimeoPlayerComponent, value: boolean) {
        this.options.autopause = value ? 1 : 0;
    })
    autopause = true;

    @Input()
    progressBar = false;

    @Input()
    pauseOnInit = true;

    @Output()
    onVideoEnd = new EventEmitter();

    @Output()
    onBufferEnd = new EventEmitter();

    @Input()
    vimeoPlayerOptions?: IVimeoPlayerOptions;

    @ViewChild('videoProgressBar') videoProgressBar?: ElementRef;

    @ViewChild('container')
    @OnChange(function (this: VimeoPlayerComponent) {
        (this as VimeoPlayerComponent).update();
    })
    container?: ElementRef;

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.cleanup();
    }

    protected override load(): void {
        if (this.app.isPlatformBrowser) {
            super.load();

            if (!this.player) {
                this.requestService.loadScriptAsync(VimeoPlayerComponent.PLAYER_API_URL).then(() => this.update());
            }
        }
    }

    init() {
        if (this.app.isPlatformBrowser) {
            if (window['Vimeo']) {
                VimeoPlayerComponent.API = window['Vimeo'];
            } else if (VimeoPlayerComponent.WAIT < VimeoPlayerComponent.MAX_WAIT) {
                VimeoPlayerComponent.WAIT++;

                this.timeoutService.setTimeout(() => this.init(), 1000);
            }

            if (
                VimeoPlayerComponent.API &&
                this.options.id &&
                (this.autoplay || this.autoplayInViewport || this.playerInit || this.useCustomControls)
            ) {
                this.cleanup();

                if (this.useCustomControls) {
                    this.setPlayer(() => this.setControlsListeners());
                } else {
                    this.setPlayer();
                }
            }
        }
    }

    override update() {
        if (this.initialized) {
            this.options.id = this.id;

            this.init();
        }
    }

    /**
     * Get player iframe URL.
     *
     * @returns {SafeResourceUrl | undefined}
     */
    get src(): SafeResourceUrl | undefined {
        const options = Object.entries(this.getVimeoOptions())
            .map(([key, val]) => `${key}=${val}`)
            .join('&');

        return this.id
            ? this.domSanitizer.bypassSecurityTrustResourceUrl(`${VimeoPlayerComponent.PLAYER_URL}${this.id}?${options}`)
            : undefined;
    }

    /**
     * Update ID.
     */
    override set id(value: string) {
        const match = value?.match(VimeoPlayerComponent.PLAYER_ID_REGEXP);
        this._id = match && match[5].length ? match[5] : value;
    }

    /**
     * Get video ID from `this.videoId` or `this.videoUrl`.
     *
     * @returns {string}
     */
    override get id(): string {
        return this._id;
    }

    /**
     * Setup player.
     *
     * @param callback {() => void}
     */
    setPlayer(callback?: () => void): void {
        if (!this.player) {
            this._setPlayer(callback);
        } else if (this.player) {
            this.player.destroy().then(() => {
                this.player = undefined;
                this._setPlayer(callback);
            });
        } else if (callback) {
            callback();
        }
    }

    protected _setPlayer(callback?: () => void) {
        if (!this.container) {
            return;
        }
        const options = this.getVimeoOptions();
        console.log(this, options)
        this.player = new window['Vimeo'].Player(this.container.nativeElement, options);

        this.player.ready().then(() => {
            this.isReady = true;

            this.detectChanges();

            this.player.on('play', () => this.onPlayEvent());
            this.player.on('pause', () => this.onPauseEvent());

            if (options.controls) {
                this.setInternalPlayerPlaySubscription();
            }

            // if video is in background mode than pause it on init and autoplay in viewport
            if (this.background) {
                // Hack for Mobile Safari
                this.renderer2.setAttribute(this.player?.element, 'playsinline', 'true');

                this.showControl = false;

                this.detectChanges();

                if (this.pauseOnInit) {
                    this.player.getPaused().then((isPaused: boolean) => {
                        if (!isPaused) {
                            this.player.pause().then(() => {
                                this.player.setCurrentTime(0);

                                if (!this.useCustomControls) {
                                    this.checkInView();
                                }
                            });
                        } else {
                            this.pause();

                            if (!this.useCustomControls) {
                                this.checkInView();
                            }
                        }
                    });
                }
            } else if (!this.useCustomControls) {
                this.checkInView();
            }

            if (callback) {
                callback();
            }

            if (this.playerInit) {
                this.playerInit.emit(this);
            }

            this.player.on('ended', () => this.onVideoEnd.emit());

            this.player.on('bufferend', () => this.onBufferEnd.emit());
        });
    }

    private onPlayEvent(inView = false) {
        this.isPlaying = true;
        this.isPaused = inView && this.autoplayOnPause ? false : false; // this will be changed in `pause` function if needed

        if (this.progressBar && this.videoProgressBar) {
            this.player.on('timeupdate', (event: IVimeoPlayerProgressEvent) => this.setProgressBar(event));
        }

        if (this.playerPlay) {
            this.playerPlay.emit(this);
        }

        this.detectChanges();
    }

    private onPauseEvent(inView = false) {
        this.isPlaying = false;
        this.isPaused = inView && !this.isPaused ? false : true; // this will be changed in `pause` function if needed

        if (this.resetOnPause) {
            this.player.setCurrentTime(0);
        }

        if (!this.background) {
            this.showControl = true;
        }

        if (this.progressBar && this.videoProgressBar) {
            this.player.off('timeupdate', (event: IVimeoPlayerProgressEvent) => this.setProgressBar(event));
        }

        if (this.playerPause) {
            this.playerPause.emit(this);
        }

        this.detectChanges();
    }

    /**
     * Play video.
     */
    override play(inView = false) {
        if (this.player) {
            this.player.ready().then(() => {
                this.player.getPaused().then((isPaused: boolean) => {
                    if (isPaused) {
                        this.player
                            .play()
                            .then(() => {
                                if (inView && this.autoplayOnPause) {
                                    this.isPaused = false;
                                    this.detectChanges();
                                }
                            })
                            .catch(error => {
                                this.app.logger.error(error);

                                if (!this.background) {
                                    this.showControl = true;
                                    this.detectChanges();
                                }
                            });
                    } else {
                        this.onPlayEvent(inView);
                    }
                });
            });
        }
    }

    /**
     * Pause video and optional reset time.
     */
    override pause(inView = false) {
        if (this.player) {
            this.player.ready().then(() => {
                this.player.getPaused().then((isPaused: boolean) => {
                    if (!isPaused) {
                        this.player.pause().then(() => {
                            if (inView && !this.isPaused) {
                                this.isPaused = false;
                                this.detectChanges();
                            }
                        });
                    } else {
                        this.onPauseEvent(inView);
                    }
                });
            });
        }
    }

    override stop() {
        if (this.player) {
            this.player.ready().then(() => {
                this.pause();
                this.player.setCurrentTime(0);
                this.isPaused = false;

                if (this.playerStop) {
                    this.playerStop.emit(this);
                }

                this.detectChanges();
            });
        }
    }

    override mute() {
        if (this.player) {
            this.player.ready().then(() => {
                this.player.setVolume(0);
                this.isMuted = true;
                this.detectChanges();
            });
        }
    }

    override unMute() {
        if (this.player) {
            this.player.ready().then(() => {
                this.player.setVolume(1);
                this.isMuted = false;
                this.detectChanges();
            });
        }
    }

    /**
     * Sets the percentage of video length watched as width of div.
     */
    setProgressBar(event: IVimeoPlayerProgressEvent) {
        if (this.videoProgressBar) {
            this.renderer2.setStyle(
                this.videoProgressBar.nativeElement,
                '--vimeo-player-progress',
                `${event.percent * 100}%`,
                RendererStyleFlags2.DashCase
            );
        }
        this.playerProgress.emit(event);
    }

    /**
     * If autoplay in viewport or autoplay.
     *
     * @returns {boolean}
     */
    protected override canPlay(): boolean {
        return (
            !!(this.autoplayInViewport || this.autoplay || this.isPlaying) &&
            ((this.autoplayOnPause && (this.isPaused === undefined || this.isPaused === true)) ||
                (!this.autoplayOnPause && this.isPaused === undefined))
        );
    }

    /**
     * Sets subscription to internal player 'play' event to emit playerPlay event on every play
     */
    private setInternalPlayerPlaySubscription() {
        this.player.on('play', data => this.playerPlay.emit(data));
    }

    /**
     * Cleanup
     */
    protected cleanup() {
        this.showControl = false;
        this.detectChanges();
        if (this.player) {
            this.player.off('play');
        }
    }

    getVideoHeight(): Promise<number> {
        if (this.player?.getVideoHeight) {
            return this.player.getVideoHeight();
        } else {
            return new Promise(resolve => resolve(0));
        }
    }

    getVideoWidth(): Promise<number> {
        if (this.player?.getVideoWidth) {
            return this.player.getVideoWidth();
        } else {
            return new Promise(resolve => resolve(0));
        }
    }

    private getVimeoOptions(): IVimeoPlayerOptions {
        return { ...this.options, ...(this.vimeoPlayerOptions || {}) };
    }
}
